"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowRightLeft, Menu, X } from "lucide-react";
import { DemoButton } from "@/components/demo-button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { MegaMenuContent } from "@/components/layout/mega-menu-content";
import { MOBILE_SECTIONS } from "@/components/layout/navbar";
import {
  NAV_ITEMS,
  FEATURED_EBOOK,
  isMegaNavItem,
  isSimpleDropdownNavItem,
  isLinkNavItem,
} from "@/lib/constants";
import { cn } from "@/lib/utils";

/**
 * Pływająca nawigacja w stylu „Sky" — jedno menu dla całej strony.
 * Linki 1:1 z NAV_ITEMS / MOBILE_SECTIONS + pasek promo.
 * `overSky`: strona zaczyna się niebem — biały tekst na szkle, po przewinięciu jasna pigułka.
 * Bez `overSky` pigułka jest jasna od początku.
 */
export function SkyNav({ overSky = false, promoHref = "/kontakt" }: { overSky?: boolean; promoHref?: string } = {}) {
  const [showPromo, setShowPromo] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const light = !overSky || scrolled || mobileOpen;

  const triggerCls = cn(
    "h-9 rounded-full bg-transparent px-4",
    light
      ? "text-foreground/75 hover:bg-black/[0.05] hover:text-foreground data-open:bg-black/[0.05] focus:bg-black/[0.05]"
      : "text-white/90 hover:bg-white/15 hover:text-white data-open:bg-white/15 data-open:hover:bg-white/15 focus:bg-white/15"
  );

  return (
    <>
      {/* Pasek promo w kolorze nieba — zlewa się z hero zamiast odcinać od niego */}
      {showPromo && (
        <div className="relative bg-[#0528F2] text-white">
          <div className="mx-auto flex max-w-5xl items-center justify-center gap-2 px-10 pb-0.5 pt-2.5 text-[13px] sm:text-sm">
            <Link
              href={promoHref}
              className="group inline-flex min-w-0 items-center gap-2.5 rounded-full bg-white/10 py-1 pl-1 pr-3 ring-1 ring-white/25 transition hover:bg-white/15"
            >
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs font-bold text-primary">
                <ArrowRightLeft className="h-3 w-3" />0 zł
              </span>
              <span className="truncate">
                <span className="font-semibold">Migracja</span>
                <span className="hidden text-white/85 sm:inline"> — przenosimy 100% danych bez żadnej utraty</span>
                <span className="text-white/85 sm:hidden"> bez utraty danych</span>
              </span>
              <ArrowRight className="h-3.5 w-3.5 shrink-0 transition group-hover:translate-x-0.5" />
            </Link>
            <button
              onClick={() => setShowPromo(false)}
              className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full text-white/70 transition hover:bg-white/10 hover:text-white sm:right-4"
              aria-label="Zamknij"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Sticky: pigułka „wisi" nad kartą nieba (hero ma ujemny margines) */}
      <header className="sticky top-0 z-[110] px-4 pt-3 md:px-6">
        <nav
          aria-label="Główna"
          className={cn(
            "mx-auto flex max-w-5xl items-center justify-between gap-4 rounded-2xl py-2 pl-4 pr-2 ring-1 backdrop-blur-xl transition-all duration-300",
            light
              ? "bg-white/85 shadow-lg shadow-black/[0.06] ring-black/[0.06]"
              : "bg-white/15 ring-white/30"
          )}
        >
          <Link href="/" aria-label="Vetina — strona główna" className="shrink-0">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/images/logo.svg"
              alt="Vetina"
              className={cn("h-7 w-auto transition", light ? "" : "brightness-0 invert")}
            />
          </Link>

          <NavigationMenu viewport={false} className="hidden md:flex">
            <NavigationMenuList className="gap-1">
              {NAV_ITEMS.main.map((item) => {
                if (isMegaNavItem(item)) {
                  return (
                    <NavigationMenuItem key={item.label} className="static">
                      <NavigationMenuTrigger className={triggerCls}>{item.label}</NavigationMenuTrigger>
                      <NavigationMenuContent className="left-1/2 -translate-x-1/2 md:left-1/2 md:right-auto">
                        <MegaMenuContent item={item} />
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  );
                }
                if (isSimpleDropdownNavItem(item)) {
                  return (
                    <NavigationMenuItem key={item.label}>
                      <NavigationMenuTrigger className={triggerCls}>{item.label}</NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="grid w-48 gap-0.5 p-1.5">
                          {item.children.map((child) => (
                            <li key={child.label}>
                              <NavigationMenuLink asChild>
                                <Link
                                  href={child.href}
                                  className="block rounded-md px-2.5 py-1.5 text-sm transition-colors hover:bg-accent"
                                >
                                  {child.label}
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  );
                }
                if (isLinkNavItem(item)) {
                  return (
                    <NavigationMenuItem key={item.label}>
                      <NavigationMenuLink asChild>
                        <Link href={item.href} className={cn("inline-flex items-center text-sm font-medium", triggerCls)}>
                          {item.label}
                        </Link>
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                  );
                }
                return null;
              })}
            </NavigationMenuList>
          </NavigationMenu>

          <div className="flex items-center gap-1.5">
            <DemoButton
              source="sky_nav"
              size="sm"
              className={cn(
                "h-9 rounded-xl px-4 text-sm font-semibold transition-colors",
                light ? "shadow-md shadow-primary/20" : "bg-white text-primary hover:bg-white/90"
              )}
              onBeforeOpen={closeMobile}
            >
              {NAV_ITEMS.cta.label}
            </DemoButton>
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className={cn(
                "grid h-9 w-9 place-items-center rounded-xl transition-colors md:hidden",
                light ? "text-foreground hover:bg-black/5" : "text-white hover:bg-white/15"
              )}
              aria-label={mobileOpen ? "Zamknij menu" : "Otwórz menu"}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>
      </header>

      {/* Menu mobilne — te same sekcje co produkcja */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="fixed inset-0 z-[100] bg-background md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <nav className="flex h-full flex-col gap-7 overflow-y-auto px-6 pb-8 pt-28">
              {MOBILE_SECTIONS.map((section) => (
                <div key={section.label}>
                  <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {section.label}
                  </p>
                  <div className="flex flex-col">
                    {section.items.map((item) => (
                      <Link
                        key={item.label}
                        href={item.href}
                        onClick={closeMobile}
                        className="py-2 text-xl font-medium text-foreground transition-colors hover:text-primary"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
              <div>
                <p className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Polecane</p>
                <div className="rounded-2xl bg-muted/40 p-4">
                  <p className="text-base font-medium">{FEATURED_EBOOK.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{FEATURED_EBOOK.description}</p>
                  <Link
                    href={FEATURED_EBOOK.href}
                    onClick={closeMobile}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary"
                  >
                    {FEATURED_EBOOK.ctaLabel}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
